/**
 * useCompare — Hook pour gérer le comparateur de 2-3 propriétés.
 *
 * Stocke les IDs sélectionnés dans localStorage pour persister entre navigations.
 * Limite à 3 biens maximum. Émet un event "realty237:compare-changed" pour les
 * composants intéressés (CompareTray sticky).
 */
import { useState, useEffect, useCallback } from 'react';

const KEY = 'realty237:compare';
const MAX = 3;
const EVT = 'realty237:compare-changed';

const readStore = () => {
  try {
    const raw = localStorage.getItem(KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.slice(0, MAX) : [];
  } catch {
    return [];
  }
};

const writeStore = (arr) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(arr));
    window.dispatchEvent(new CustomEvent(EVT, { detail: arr }));
  } catch {/* noop */}
};

export const useCompare = () => {
  const [items, setItems] = useState(readStore);

  useEffect(() => {
    const sync = () => setItems(readStore());
    window.addEventListener(EVT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(EVT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const has = useCallback((id) => items.some((p) => (p._id || p.id) === id), [items]);

  const add = useCallback((property) => {
    if (!property) return { ok: false, reason: 'invalid' };
    const id = property._id || property.id;
    const current = readStore();
    if (current.some((p) => (p._id || p.id) === id)) return { ok: false, reason: 'exists' };
    if (current.length >= MAX) return { ok: false, reason: 'limit' };
    const next = [...current, {
      _id: id, id,
      title: property.title,
      price: property.price,
      city: property.city,
      neighborhood: property.neighborhood,
      images: property.images?.slice(0, 1) || [],
      purpose: property.purpose,
      type: property.type,
      bedrooms: property.bedrooms,
      bathrooms: property.bathrooms,
      area: property.area,
      landSize: property.landSize,
      features: property.features || [],
      verified: property.verified,
    }];
    writeStore(next);
    return { ok: true };
  }, []);

  const remove = useCallback((id) => {
    const next = readStore().filter((p) => (p._id || p.id) !== id);
    writeStore(next);
  }, []);

  const clear = useCallback(() => writeStore([]), []);

  return { items, add, remove, clear, has, max: MAX, count: items.length };
};

export default useCompare;
